'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Project } from '@/lib/types'
import { parseDateInputValue, formatDateInputValue } from '@/lib/dates'
import {
  IMPORTANCE_OPTIONS, 
  URGENCY_OPTIONS, 
  SCOPE_OPTIONS, 
  ENJOYMENT_OPTIONS, 
  STATUS_OPTIONS,
  ProjectOption
} from './constants'

interface ProjectFormProps {
  project?: Partial<Project>  // For editing existing projects
  onSave: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void
  onCancel: () => void
  onChange?: (project: Partial<Project>) => void  // For real-time preview
  isSubmitting?: boolean
  /**
   * Show the "Last worked on" seed field. Off by default, and deliberately NOT
   * passed by the edit drawer: the pre-registration (§8) says seeds are entered
   * from recollection before any outcome is known and "not revised afterward".
   * Making them un-editable in the UI is what enforces that — correcting a typo
   * means deleting and re-creating the project, which leaves a visible trace.
   */
  showWorkedOnSeed?: boolean
}

export default function ProjectForm({ project, onSave, onCancel, onChange, isSubmitting = false, showWorkedOnSeed = false }: ProjectFormProps) {
  const [formData, setFormData] = useState({
    title: project?.title || '',
    description: project?.description || '',
    status: (project?.status || 'Current') as 'Current' | 'Future' | 'Past',
    importance: project?.importance || 2,
    urgency: project?.urgency || 2,
    scope: project?.scope || 2,
    enjoyment: project?.enjoyment || 3
  })

  // Held as the raw input string rather than a Date, so a half-typed value
  // ("2026-0") doesn't momentarily parse into something wrong.
  const [workedOnInput, setWorkedOnInput] = useState(
    project?.lastWorkedAt ? formatDateInputValue(project.lastWorkedAt) : ''
  )

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Read the clock ONCE, at mount, rather than on every render. Two reasons:
  // reading it during render is impure (the lint rule is right), and `today` and
  // the day count below must agree with each other — a form that recomputed
  // "now" per render could show a max of one day and a count based on another.
  // Mirrors the injectable `now` that scoring.ts uses for the same reason.
  const [mountedAt] = useState(() => new Date())

  const today = useMemo(() => formatDateInputValue(mountedAt), [mountedAt])

  // Echo the seed back as a day count. Typing a date and being told "≈ 62 days
  // untouched" is the check that catches a seed off by a month or a year.
  const seedDaysAgo = useMemo(() => {
    const seed = parseDateInputValue(workedOnInput)
    if (!seed) return null
    return Math.max(0, Math.floor((mountedAt.getTime() - seed.getTime()) / 86_400_000))
  }, [workedOnInput, mountedAt])

  // The payload the form actually emits. A blank or unparseable seed is simply
  // omitted, which leaves `lastWorkedAt` undefined so scoring falls back to
  // `createdAt` — never the epoch (infinite neglect) and never now (none).
  const payload = useMemo(() => {
    if (!showWorkedOnSeed) return formData
    const seed = parseDateInputValue(workedOnInput)
    return seed ? { ...formData, lastWorkedAt: seed } : formData
  }, [formData, workedOnInput, showWorkedOnSeed])

  // Real-time preview: call onChange when form data changes
  useEffect(() => {
    if (onChange) {
      onChange(payload)
    }
  }, [payload, onChange])

  const handleInputChange = useCallback((field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }, [errors])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.title.trim()) {
      newErrors.title = 'Project title is required'
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Project description is required'
    }

    // A seed that is silently wrong is worse than one that is refused — it would
    // corrupt the neglect clock with nothing on screen to reveal it.
    if (showWorkedOnSeed && workedOnInput.trim()) {
      const seed = parseDateInputValue(workedOnInput)
      if (!seed) {
        newErrors.workedOn = 'Enter a real date (YYYY-MM-DD), or leave blank'
      } else if (seed.getTime() > new Date().setHours(23, 59, 59, 999)) {
        newErrors.workedOn = 'You cannot have last worked on this in the future'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (validateForm()) {
      onSave(payload)
    }
  }

    const renderRadioGroup = <T extends string | number>(
    label: string,
    options: ProjectOption<T>[],
    value: T,
    onChange: (value: T) => void
    ) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
        {label}
        </label>
        <div className="grid grid-cols-1 gap-2">
        {options.map((option) => (
            <label
            key={String(option.value)}
            className={`relative flex cursor-pointer rounded-lg border p-3 shadow-sm transition-colors ${
                value === option.value
                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-300 bg-white hover:border-gray-400 dark:border-gray-600 dark:bg-gray-700'
            }`}
            >
            <input
                type="radio"
                name={label}
                value={String(option.value)}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                className="sr-only"
            />
            <div className="flex flex-1 items-center">
                {option.emoji && (
                <span className="mr-3 text-lg">{option.emoji}</span>
                )}
                <div className="flex flex-col">
                <span className={`block text-sm font-medium ${
                    value === option.value ? 'text-primary-900 dark:text-primary-100' : 'text-gray-900 dark:text-gray-100'
                }`}>
                    {option.label}
                </span>
                <span className={`block text-xs ${
                    value === option.value ? 'text-primary-700 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'
                }`}>
                    {option.description}
                </span>
                </div>
            </div>
            {value === option.value && (
                <div className="absolute -inset-px rounded-lg border-2 border-primary-600 pointer-events-none" />
            )}
            </label>
        ))}
        </div>
    </div>
    )

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Project Title *
        </label>
        <input
          type="text"
          id="title"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white ${
            errors.title 
              ? 'ring-red-300 focus:ring-red-500' 
              : 'ring-gray-300 focus:ring-primary-600 dark:ring-gray-600 dark:focus:ring-primary-500'
          }`}
          placeholder="What needs to be done?"
        />
        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description *
        </label>
        <textarea
          id="description"
          rows={3}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white ${
            errors.description
              ? 'ring-red-300 focus:ring-red-500'
              : 'ring-gray-300 focus:ring-primary-600 dark:ring-gray-600 dark:focus:ring-primary-500'
          }`}
          placeholder="Describe the next action or outcome you want..."
        />
        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
      </div>

      {/* Status */}
      <div>
        <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Status
        </label>
        <select
          id="status"
          value={formData.status}
          onChange={(e) => handleInputChange('status', e.target.value)}
          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:focus:ring-primary-500"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label} - {option.description}
            </option>
          ))}
        </select>
      </div>

      {/* Last worked on — seeds the neglect clock (add-only; see showWorkedOnSeed) */}
      {showWorkedOnSeed && (
        <div>
          <label htmlFor="workedOn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Last worked on
          </label>
          <input
            type="date"
            id="workedOn"
            max={today}
            value={workedOnInput}
            onChange={(e) => {
              setWorkedOnInput(e.target.value)
              if (errors.workedOn) setErrors(prev => ({ ...prev, workedOn: '' }))
            }}
            className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white ${
              errors.workedOn
                ? 'ring-red-300 focus:ring-red-500'
                : 'ring-gray-300 focus:ring-primary-600 dark:ring-gray-600 dark:focus:ring-primary-500'
            }`}
          />
          {errors.workedOn ? (
            <p className="mt-1 text-sm text-red-600">{errors.workedOn}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {seedDaysAgo === null
                ? 'Leave blank if you worked on this today. Otherwise set when you last did real work — this seeds the neglect clock, and neglect is what raises urgency.'
                : `≈ ${seedDaysAgo} day${seedDaysAgo === 1 ? '' : 's'} untouched as of today.`}
            </p>
          )}
        </div>
      )}

      {/* Importance */}
      {renderRadioGroup('Importance', IMPORTANCE_OPTIONS, formData.importance, (value) => handleInputChange('importance', value))}

      {/* Urgency */}
      {renderRadioGroup('Urgency', URGENCY_OPTIONS, formData.urgency, (value) => handleInputChange('urgency', value))}

      {/* Scope */}
      {renderRadioGroup('Scope', SCOPE_OPTIONS, formData.scope, (value) => handleInputChange('scope', value))}

      {/* Enjoyment */}
      {renderRadioGroup('Enjoyment', ENJOYMENT_OPTIONS, formData.enjoyment, (value) => handleInputChange('enjoyment', value))}

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-6">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSubmitting}
          className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : 'Save Project'}
        </button>
      </div>
    </div>
  )
}